/**
 * The contract every data layer implements.
 *
 * A layer owns one source. It declares a time window from its manifest, loads
 * day slices lazily, and turns the clock cursor into deck.gl layers. It never
 * touches the map, the HUD or another layer.
 *
 * Three rules, enforced by the registry rather than by each layer remembering:
 *
 *  1. `load()` must not throw. A source that fails marks itself unavailable and
 *     the rest of the map carries on.
 *  2. Only day slices inside the current window are fetched, and each is
 *     fetched at most once.
 *  3. `render(cursor)` is called on every frame and must be cheap. Do the
 *     filtering, not the parsing, there.
 */

export const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  UNAVAILABLE: "unavailable",
};

export class Layer {
  /** Stable key, also used in the DOM and the URL. */
  static key = "";
  /** Shown in the toggle list. */
  static label = "";
  /** One line, shown under the label. */
  static description = "";

  /**
   * How often to re-read this source while the page is open, in ms.
   *
   * Zero — the default — means never, and most layers should keep it. A layer
   * is worth polling only if it can change while someone is watching AND is
   * small enough that asking again is cheap. The server caches every one of
   * these, so polling faster than its TTL just re-downloads an identical
   * answer at full cost: nothing is saved by asking twice inside one window.
   */
  static refreshMs = 0;

  constructor({ basePath = "./data" } = {}) {
    this.basePath = basePath;
    this.status = STATUS.IDLE;
    this.detail = "";
    this.enabled = true;
    this.manifest = null;
    this._slices = new Map(); // dayKey -> array
    this._inFlight = new Map();
    this.visibleCount = 0;
    this.hiddenKinds = new Set();
  }

  get key() {
    return this.constructor.key;
  }

  get label() {
    return this.constructor.label;
  }

  /** Total records held in memory. */
  get loadedCount() {
    let total = 0;
    for (const slice of this._slices.values()) total += slice.length;
    return total;
  }

  /** Read the manifest. Returns {start, end} or null when unavailable. */
  async load() {
    this.status = STATUS.LOADING;
    try {
      const response = await fetch(`${this.basePath}/${this.key}/manifest.json`);
      if (!response.ok) throw new Error(`manifest ${response.status}`);
      this.manifest = await response.json();
      if (!this.manifest.days?.length) {
        this.status = STATUS.UNAVAILABLE;
        this.detail = "No data slices";
        return null;
      }
      this.status = STATUS.READY;
      this.detail = `${this.manifest.count} records`;
      return { start: this.manifest.start, end: this.manifest.end };
    } catch (error) {
      // Rule 1: never throw. A missing source must not take the map down.
      this.status = STATUS.UNAVAILABLE;
      this.detail = `Unavailable (${error.message})`;
      return null;
    }
  }

  /** Fetch one day slice, at most once. Safe to call repeatedly. */
  async ensureSlice(dayKey) {
    if (this._slices.has(dayKey)) return this._slices.get(dayKey);
    if (this._inFlight.has(dayKey)) return this._inFlight.get(dayKey);

    const promise = (async () => {
      try {
        const response = await fetch(`${this.basePath}/${this.key}/${dayKey}.json`);
        if (!response.ok) throw new Error(String(response.status));
        const body = await response.json();
        const rows = this.parseSlice(body);
        this._slices.set(dayKey, rows);
        return rows;
      } catch {
        // A missing day is normal: reporting lags, so recent days may not exist.
        this._slices.set(dayKey, []);
        return [];
      } finally {
        this._inFlight.delete(dayKey);
      }
    })();

    this._inFlight.set(dayKey, promise);
    return promise;
  }

  /** Override to reshape a slice payload into render-ready rows. */
  parseSlice(body) {
    return body.points ?? [];
  }

  /** All loaded rows, flattened. */
  rows() {
    const all = [];
    for (const slice of this._slices.values()) all.push(...slice);
    return all;
  }

  /**
   * Zoom levels at which render()'s output changes shape.
   *
   * The feeds slot is not rebuilt on zoom — deliberately, see main.js — so a
   * layer whose render() reads the zoom goes stale until the next full paint,
   * which can be the one-minute refresh tick. A layer that gates on zoom
   * declares the thresholds here instead, and main.js repaints the feeds slot
   * when a move ends on the other side of one. A getter, not a field: a field
   * initialised here would shadow a subclass's getter override.
   */
  get zoomThresholds() {
    return [];
  }

  /** Override: return an array of deck.gl layers for this cursor position. */
  render() {
    return [];
  }

  /**
   * Override: return legend entries [{rgb, label, note, key}].
   *
   * An entry with a `key` is clickable in the legend and hides that kind on
   * the map. An entry without one is a plain swatch. The distinction is not
   * bookkeeping — it is the rule for whether a filter should exist at all: a
   * legend row that covers the layer's ENTIRE output would only duplicate the
   * layer toggle sitting a few centimetres above it, so those stay unkeyed.
   * A filter earns its place by doing something the toggle cannot.
   */
  legend() {
    return [];
  }

  /**
   * Legend kinds the reader has switched off. Empty means everything shows.
   *
   * Lives on the layer rather than in the DOM because the legend is rebuilt
   * wholesale on every theme change — state kept in the rows themselves would
   * be silently discarded the first time someone pressed Y.
   */
  isHidden(kind) {
    return this.hiddenKinds.has(kind);
  }

  /** Narrow a list to the kinds still showing. */
  visibleOf(rows, kindOf) {
    if (this.hiddenKinds.size === 0) return rows;
    return rows.filter((row) => !this.hiddenKinds.has(kindOf(row)));
  }

  /** Switch one legend kind off, or back on. Returns true if now hidden. */
  toggleKind(key) {
    if (this.hiddenKinds.delete(key)) return false;
    this.hiddenKinds.add(key);
    return true;
  }

  /**
   * A value that changes whenever the filter does, for deck's updateTriggers.
   *
   * The tiled layers build their sublayers inside a `renderSubLayers` closure,
   * which deck caches per tile. Without this in the triggers, a tile already
   * on screen keeps drawing the kinds you just hid until something else
   * happens to invalidate it — the same trap the greenery zoom gate fell into.
   */
  get filterStamp() {
    return [...this.hiddenKinds].sort().join("|");
  }
}
